import React, { useState, useRef, useEffect, useCallback } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Upload, FileText, Loader2, CheckCircle, Download, RefreshCw, Eye,
  FileSpreadsheet, Clock, Hash, Target, ArrowRight, ChevronDown, ChevronUp,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/services/api';

const COUNTRIES = ['China','Russia','Belarus','North Korea','Iran','Syria','Myanmar','Cuba','Venezuela','Libya','Somalia','Sudan','Yemen','Afghanistan','Pakistan','Iraq','Lebanon'];
const COMPLIANCES = ['EU-RoHS','CN-RoHS','REACH','PFAS'];
const TEMP_GRADES = ['Commercial','Commercial Extended','Industrial','Automotive','Military','High Temperature','Space','Medical','Telecom','Other'];
const QUALIFICATIONS = ['AEC','AUTO-QMS','AUTO-SAFETY','EMC','IEC-COMP','JEDEC','SAFETY-CERT','MIL','SPEC'];

const PillSelect = ({ items, selected, onToggle, colorOn, colorOff, testPrefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map(v => (
      <button key={v} onClick={() => onToggle(v)}
        className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${selected.includes(v) ? colorOn : colorOff}`}
        data-testid={`${testPrefix}-${v}`}>
        {v} {selected.includes(v) ? '\u2713' : ''}
      </button>
    ))}
  </div>
);

const DesignValidate = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [bomName, setBomName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [loadingUploads, setLoadingUploads] = useState(true);
  const [showCriteria, setShowCriteria] = useState(true);

  // Criteria state
  const [excludeCountries, setExcludeCountries] = useState([]);
  const [compliances, setCompliances] = useState([]);
  const [tempGrade, setTempGrade] = useState([]);
  const [qualifications, setQualifications] = useState([]);

  const toggle = (arr, setArr, val) => setArr(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);

  const fetchUploads = useCallback(async () => {
    try {
      const res = await api.get('/design/validate/list');
      setUploads(res.data.uploads || []);
    } catch { /* silent */ }
    setLoadingUploads(false);
  }, []);

  useEffect(() => {
    fetchUploads();
    const interval = setInterval(fetchUploads, 5000);
    return () => clearInterval(interval);
  }, [fetchUploads]);

  const handleFileSelect = (e) => { const f = e.target.files?.[0]; if (f) setFile(f); };
  const handleDrop = (e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); };

  const handleStartValidation = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bom_name', bomName);
      formData.append('exclude_countries', excludeCountries.join(','));
      formData.append('compliances', compliances.join(','));
      formData.append('temperature_grade', tempGrade.join(','));
      formData.append('qualifications', qualifications.join(','));
      const res = await api.post('/design/validate/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.error) {
        toast.error(res.data.error);
      } else {
        toast.success(`Validation started for ${res.data.name} (${res.data.line_count} lines)`);
        setFile(null);
        setBomName('');
        fetchUploads();
      }
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const handleRevalidate = async (projectId) => {
    try {
      await api.post(`/design/validate/${projectId}/revalidate`);
      toast.success('Re-validation started');
      fetchUploads();
    } catch { toast.error('Re-validation failed'); }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Layout title="Design Review" subtitle="Upload and validate an existing BOM against risks, compliances & alternates">
      <div className="max-w-5xl mx-auto space-y-6" data-testid="design-validate-page">

        {/* Upload Card */}
        <Card className="bg-white border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
            <h2 className="text-white font-semibold text-lg flex items-center gap-2">
              <Upload className="h-5 w-5" /> Upload BOM for Validation
            </h2>
            <p className="text-green-100 text-sm mt-0.5">Upload a CSV with your Bill of Materials. We'll match, enrich, and validate every line item.</p>
          </div>
          <CardContent className="p-6 space-y-4">
            {/* BOM Name */}
            <div>
              <Label className="text-xs font-medium text-gray-700">BOM Name</Label>
              <Input value={bomName} onChange={e => setBomName(e.target.value)} placeholder="e.g. Power Supply Board Rev 3.2"
                className="mt-1 h-9 text-sm" data-testid="input-bom-name" />
            </div>

            {/* Expected fields */}
            <div className="flex items-start gap-3 text-sm bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <FileSpreadsheet className="h-4 w-4 text-gray-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-gray-700">Expected CSV columns</p>
                <p className="text-gray-500 mt-0.5">
                  <span className="font-medium text-gray-700">MPN</span>, <span className="font-medium text-gray-700">CPN</span> (either is required) &middot;
                  <span className="text-gray-400 ml-1">Make</span> (optional) &middot;
                  <span className="text-gray-400 ml-1">Annual Requirement</span> (optional)
                </p>
              </div>
            </div>

            {/* Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
                ${file ? 'border-green-400 bg-green-50/50' : 'border-gray-300 hover:border-green-400 hover:bg-green-50/30'}`}
              onClick={() => fileRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              data-testid="upload-drop-zone"
            >
              <input ref={fileRef} type="file" className="hidden" accept=".csv"
                onChange={handleFileSelect} data-testid="file-input" />
              {file ? (
                <div className="space-y-2">
                  <CheckCircle className="h-9 w-9 text-green-500 mx-auto" />
                  <p className="font-semibold text-green-700">{file.name}</p>
                  <p className="text-sm text-green-600">{(file.size / 1024).toFixed(1)} KB</p>
                  <Button variant="outline" size="sm" className="mt-2 text-xs" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                    Change File
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <FileText className="h-9 w-9 text-gray-400 mx-auto" />
                  <p className="font-medium text-gray-700">Drop your BOM CSV here or click to browse</p>
                  <p className="text-xs text-gray-400">Accepts .csv files</p>
                </div>
              )}
            </div>

            {/* Validation Criteria */}
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="validation-criteria">
              <button className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                onClick={() => setShowCriteria(!showCriteria)} data-testid="toggle-criteria">
                <span className="text-sm font-medium text-gray-700">Validation Criteria</span>
                <div className="flex items-center gap-2">
                  {(excludeCountries.length + compliances.length + tempGrade.length + qualifications.length) > 0 && (
                    <Badge className="bg-green-100 text-green-700 text-[10px]">{excludeCountries.length + compliances.length + tempGrade.length + qualifications.length} selected</Badge>
                  )}
                  {showCriteria ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </div>
              </button>
              {showCriteria && (
                <div className="p-4 space-y-4 border-t border-gray-200">
                  {/* Exclude Country of Origin */}
                  <div>
                    <Label className="text-xs font-medium">Exclude Country of Origin</Label>
                    <div className="mt-1.5">
                      <PillSelect items={COUNTRIES} selected={excludeCountries}
                        onToggle={v => toggle(excludeCountries, setExcludeCountries, v)}
                        colorOn="bg-red-100 text-red-800 border-red-300" colorOff="bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                        testPrefix="country" />
                    </div>
                  </div>

                  {/* Compliances */}
                  <div>
                    <Label className="text-xs font-medium">Compliances</Label>
                    <div className="mt-1.5">
                      <PillSelect items={COMPLIANCES} selected={compliances}
                        onToggle={v => toggle(compliances, setCompliances, v)}
                        colorOn="bg-green-100 text-green-800 border-green-300" colorOff="bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                        testPrefix="compliance" />
                    </div>
                  </div>

                  {/* Temperature Grade */}
                  <div>
                    <Label className="text-xs font-medium">Temperature Grade</Label>
                    <div className="mt-1.5">
                      <PillSelect items={TEMP_GRADES} selected={tempGrade}
                        onToggle={v => toggle(tempGrade, setTempGrade, v)}
                        colorOn="bg-blue-100 text-blue-800 border-blue-300" colorOff="bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                        testPrefix="tempgrade" />
                    </div>
                  </div>

                  {/* Qualifications */}
                  <div>
                    <Label className="text-xs font-medium">Qualifications</Label>
                    <div className="mt-1.5">
                      <PillSelect items={QUALIFICATIONS} selected={qualifications}
                        onToggle={v => toggle(qualifications, setQualifications, v)}
                        colorOn="bg-purple-100 text-purple-800 border-purple-300" colorOff="bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                        testPrefix="qual" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" className="text-xs text-gray-500 gap-1"
                onClick={() => { const a = document.createElement('a'); a.href = '/samples/Sample_Design.csv'; a.download = 'Design_Template.csv'; a.click(); }}
                data-testid="btn-download-template">
                <Download className="h-3.5 w-3.5" /> Download Template (.csv)
              </Button>
              <Button onClick={handleStartValidation} disabled={!file || uploading}
                className="bg-green-600 hover:bg-green-700 text-white gap-1.5" data-testid="btn-start-validation">
                {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Processing...</> : <>Start Validation <ArrowRight className="h-4 w-4" /></>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Uploads */}
        <div data-testid="recent-uploads-section">
          <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-500" /> Recent Uploads
          </h3>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-0">
              {loadingUploads ? (
                <div className="flex items-center justify-center py-12 text-gray-400">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
                </div>
              ) : uploads.length === 0 ? (
                <div className="text-center py-12 text-gray-400 text-sm">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  No uploads yet. Upload a BOM CSV above to get started.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50/80 text-xs">
                        <TableHead className="font-semibold">BOM Name</TableHead>
                        <TableHead className="font-semibold">Upload Date</TableHead>
                        <TableHead className="font-semibold">Last Validated</TableHead>
                        <TableHead className="font-semibold text-center"><Hash className="h-3 w-3 inline mr-0.5" />Lines</TableHead>
                        <TableHead className="font-semibold text-center"><Target className="h-3 w-3 inline mr-0.5" />Match Rate</TableHead>
                        <TableHead className="font-semibold text-center">Status</TableHead>
                        <TableHead className="font-semibold text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {uploads.map((u) => {
                        const isProcessing = u.status === 'processing';
                        const matchRate = u.health_scorecard?.match_rate;
                        return (
                          <TableRow key={u.project_id} className="hover:bg-gray-50 text-xs" data-testid={`upload-row-${u.project_id}`}>
                            <TableCell className="font-medium text-gray-800 max-w-[220px] truncate" title={u.name}>
                              <FileSpreadsheet className="h-3.5 w-3.5 inline mr-1.5 text-gray-400" />
                              {u.name || u.original_filename || 'Untitled'}
                            </TableCell>
                            <TableCell className="text-gray-500">{formatDate(u.created_at)}</TableCell>
                            <TableCell className="text-gray-500">{formatDate(u.updated_at)}</TableCell>
                            <TableCell className="text-center font-medium">{u.line_count ?? u.match_summary?.total ?? '-'}</TableCell>
                            <TableCell className="text-center">
                              {matchRate != null ? (
                                <Badge className={`text-[10px] ${matchRate >= 80 ? 'bg-green-100 text-green-800' : matchRate >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                                  {matchRate}%
                                </Badge>
                              ) : <span className="text-gray-300">-</span>}
                            </TableCell>
                            <TableCell className="text-center">
                              {isProcessing ? (
                                <Badge className="bg-blue-100 text-blue-700 text-[10px] gap-1">
                                  <Loader2 className="h-2.5 w-2.5 animate-spin" /> Processing
                                </Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-700 text-[10px] gap-1">
                                  <CheckCircle className="h-2.5 w-2.5" /> Processed
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right space-x-1">
                              {!isProcessing && (
                                <>
                                  <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 px-2"
                                    onClick={() => navigate(`/design-center/${u.project_id}`)}
                                    data-testid={`btn-view-${u.project_id}`}>
                                    <Eye className="h-3 w-3" /> View
                                  </Button>
                                  <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 px-2"
                                    onClick={() => handleRevalidate(u.project_id)}
                                    data-testid={`btn-revalidate-${u.project_id}`}>
                                    <RefreshCw className="h-3 w-3" /> Re-validate
                                  </Button>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default DesignValidate;
